<template>
  <div>
    <Header />
    <v-container>

      <!-- Page header -->
      <v-row class="mb-4" align="center">
        <v-col cols="12" sm="auto">
          <h2 class="title">
            <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
            Stickerei Produkte
          </h2>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Suchen..."
            hide-details
            dense
            outlined
            clearable
            style="min-width: 220px;"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn color="primary" @click="openForm()">
            <v-icon left>mdi-plus</v-icon>
            Produkt hinzufügen
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn @click="fetchProducts" :loading="loading">
            <v-icon left>mdi-refresh</v-icon>
            Aktualisieren
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="products"
        :search="search"
        :loading="loading"
        class="elevation-1"
        no-data-text="Keine Produkte vorhanden"
      >
        <template #[`item.actions`]="{ item }">
          <v-btn icon small @click="openForm(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="confirmDelete(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-container>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="form.show" max-width="480px" @keydown.esc="form.show = false">
      <v-card>
        <v-card-title>{{ form.id ? 'Produkt bearbeiten' : 'Neues Produkt' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="form.valid">
            <v-text-field
              v-model="form.name"
              label="Name *"
              outlined dense
              :rules="[v => !!v || 'Pflichtfeld']"
              class="mb-2"
            ></v-text-field>
            <v-text-field
              v-model="form.sku"
              label="SKU *"
              outlined dense
              :rules="[v => !!v || 'Pflichtfeld']"
              class="mb-2"
            ></v-text-field>
            <v-text-field
              v-model="form.ean"
              label="EAN"
              outlined dense
              class="mb-2"
            ></v-text-field>
            <v-select
              v-model="form.defaultFont"
              :items="fontOptions"
              label="Standard-Font (Fallback)"
              outlined dense
              clearable
              class="mb-2"
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="form.show = false" :disabled="form.saving">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveProduct" :loading="form.saving" :disabled="!form.valid">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm -->
    <v-dialog v-model="deleteDialog.show" max-width="360px">
      <v-card>
        <v-card-title>Produkt löschen?</v-card-title>
        <v-card-text>
          <strong>{{ deleteDialog.item && deleteDialog.item.name }}</strong>
          ({{ deleteDialog.item && deleteDialog.item.sku }}) wirklich löschen?
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="deleteDialog.show = false">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="deleteProduct" :loading="deleteDialog.deleting">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Header from "@/views/private/header";
import productionService from "@/services/ProductionService";

export default {
  name: "EmbroideryProducts",
  components: { Header },
  data: () => ({
    loading: false,
    search: "",
    products: [],
    headers: [
      { text: "Name",          value: "name"        },
      { text: "SKU",           value: "sku"         },
      { text: "EAN",           value: "ean"         },
      { text: "Standard-Font", value: "defaultFont" },
      { text: "",              value: "actions", sortable: false, align: "end" },
    ],
    fontOptions: ['Amatic', 'Londrina', 'Garamond', 'Stay Crisp'],
    form: {
      show: false,
      valid: false,
      saving: false,
      id: null,
      name: "",
      sku: "",
      ean: "",
      defaultFont: null,
    },
    deleteDialog: {
      show: false,
      deleting: false,
      item: null,
    },
    snackbar: { show: false, text: "", color: "success" },
  }),

  async mounted() {
    await this.fetchProducts();
  },

  methods: {
    async fetchProducts() {
      this.loading = true;
      try {
        const res = await productionService.getEmbroideryProducts();
        this.products = res.data;
      } catch {
        this.notify("Fehler beim Laden der Produkte", "error");
      } finally {
        this.loading = false;
      }
    },

    openForm(item = null) {
      this.form.id = item ? item.id : null;
      this.form.name = item ? item.name : "";
      this.form.sku = item ? item.sku : "";
      this.form.ean = item ? (item.ean || "") : "";
      this.form.defaultFont = item ? (item.defaultFont || null) : null;
      this.form.saving = false;
      this.form.show = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },

    async saveProduct() {
      if (!this.$refs.form.validate()) return;
      this.form.saving = true;
      const payload = { name: this.form.name, sku: this.form.sku, ean: this.form.ean || null, defaultFont: this.form.defaultFont || null };
      try {
        if (this.form.id) {
          await productionService.updateEmbroideryProduct(this.form.id, payload);
          this.notify("Produkt aktualisiert");
        } else {
          await productionService.createEmbroideryProduct(payload);
          this.notify("Produkt erstellt");
        }
        this.form.show = false;
        await this.fetchProducts();
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Speichern", "error");
      } finally {
        this.form.saving = false;
      }
    },

    confirmDelete(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.deleting = false;
      this.deleteDialog.show = true;
    },

    async deleteProduct() {
      this.deleteDialog.deleting = true;
      try {
        await productionService.deleteEmbroideryProduct(this.deleteDialog.item.id);
        this.deleteDialog.show = false;
        this.notify("Produkt gelöscht");
        await this.fetchProducts();
      } catch {
        this.notify("Fehler beim Löschen", "error");
      } finally {
        this.deleteDialog.deleting = false;
      }
    },

    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>
